import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import {
  LayoutDashboard,
  Scissors,
  Image,
  Star,
  CalendarDays,
  MessageSquare,
  LogOut,
  Plus,
  Trash2,
  Pencil,
  X,
  Save,
  Upload,
  Loader2,
  Bell,
  CheckCircle,
  Sparkles,
  Megaphone,
  ExternalLink,
  Copy,
  Eye,
  EyeOff,
  Send,
  Phone,
  Users,
  Mail,
  CalendarPlus,
  AlertTriangle,
  RotateCcw,
} from "lucide-react";
import { API, SITE_URL, authHeaders, resolveUrl, ImageUploadField } from "./shared";

const GalleryTab = () => {
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [category, setCategory] = useState("braids");
  const galleryFileRef = useRef(null);

  const fetchGallery = useCallback(async () => {
    const res = await axios.get(`${API}/gallery`);
    setImages(res.data);
  }, []);

  useEffect(() => { fetchGallery(); }, [fetchGallery]);

  const handleUploadAndSave = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) {
      toast.error("File too large. Max 50MB.");
      return;
    }
    setUploading(true);
    try {
      // Step 1: Upload file
      const formData = new FormData();
      formData.append("file", file);
      const uploadRes = await axios.post(`${API}/admin/upload`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Step 2: Save to gallery immediately
      const mediaType = file.type.startsWith("video/") ? "video" : "image";
      await axios.post(`${API}/admin/gallery`, {
        src: uploadRes.data.url,
        alt: file.name.split(".")[0].replace(/[-_]/g, " "),
        category,
        media_type: mediaType,
      }, authHeaders());
      toast.success("Added to gallery!");
      fetchGallery();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (galleryFileRef.current) galleryFileRef.current.value = "";
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this image?")) return;
    try {
      await axios.delete(`${API}/admin/gallery/${id}`, authHeaders());
      toast.success("Image deleted");
      fetchGallery();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  return (
    <div data-testid="admin-gallery-tab">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-serif text-2xl text-brand-text">Gallery ({images.length})</h2>
      </div>

      {/* One-step upload */}
      <div className="border border-brand-border bg-brand-surface p-5 mb-6" data-testid="gallery-upload-form">
        <p className="font-sans text-xs text-brand-gold font-semibold mb-3 uppercase tracking-wider">Upload Photos & Videos</p>
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-end">
          <div className="flex-1 w-full">
            <label className="font-sans text-xs text-brand-text-muted mb-1 block">Category</label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="bg-brand-bg border-brand-border text-brand-text rounded-none" data-testid="gallery-category-select">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="braids">Braids</SelectItem>
                <SelectItem value="cornrows">Cornrows</SelectItem>
                <SelectItem value="twists">Twists</SelectItem>
                <SelectItem value="crochet">Crochet</SelectItem>
                <SelectItem value="locs">Faux Locs</SelectItem>
                <SelectItem value="mens">Men's</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 w-full">
            <label className="font-sans text-xs text-brand-text-muted mb-1 block">Choose file to upload</label>
            <input
              ref={galleryFileRef}
              type="file"
              accept="image/*,video/*"
              onChange={handleUploadAndSave}
              disabled={uploading}
              data-testid="gallery-file-input"
              className="block w-full text-sm text-brand-text-muted file:mr-4 file:py-2 file:px-4 file:border file:border-brand-border file:text-sm file:font-sans file:font-semibold file:bg-brand-surface file:text-brand-gold hover:file:bg-brand-gold hover:file:text-black file:cursor-pointer file:transition-colors file:rounded-none cursor-pointer"
            />
          </div>
        </div>
        {uploading && <p className="font-sans text-xs text-brand-gold mt-2 flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> Uploading and saving...</p>}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {images.map((img) => (
          <div key={img.id} className="relative group border border-brand-border overflow-hidden" data-testid={`admin-gallery-${img.id}`}>
            {img.media_type === "video" ? (
              <video src={resolveUrl(img.src)} className="w-full aspect-square object-cover" controls playsInline preload="metadata" />
            ) : (
              <img src={resolveUrl(img.src)} alt={img.alt} className="w-full aspect-square object-cover" />
            )}
            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2">
              <span className="font-sans text-xs text-brand-text-muted capitalize">{img.category} {img.media_type === "video" && "· Video"}</span>
              <button onClick={() => handleDelete(img.id)} data-testid={`delete-gallery-${img.id}`} className="p-2 bg-red-500/20 border border-red-400 text-red-400 hover:bg-red-500/40 transition-colors">
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default GalleryTab;
